import { ChevronLeft, ChevronRight, Lock, Unlock } from 'lucide-react';
import { useRef } from 'react';

export const DateNav = ({ dates, selectedDate, onDateSelect, lockedDate, onLockToggle }) => {
  const scrollRef = useRef(null);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return {
      day: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
      date: d.getDate(),
      month: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      full: d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
    };
  };

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 300, behavior: 'smooth' });
    }
  };

  // Pass through whatever the parent gave us — no hardcoded month filter.
  const displayDates = dates;

  // LOCKED MODE — show a single large card, disable all navigation.
  if (lockedDate) {
    const lockedEntry = displayDates.find(d => d.date === lockedDate);
    const formatted = lockedEntry ? formatDate(lockedEntry.date) : null;
    return (
      <nav data-testid="date-navigation-locked" className="sticky top-0 z-40 backdrop-blur-sm" style={{ background: 'rgba(12,6,6,0.75)' }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div
            onDoubleClick={() => onLockToggle(lockedDate)}
            className="relative flex items-center justify-center py-6 cursor-pointer select-none"
            title="Double-click to unlock"
            data-testid="locked-date-card"
          >
            <div
              className="relative flex flex-col items-center justify-center px-12 py-5 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, #b91c1c 0%, #8b0000 100%)',
                boxShadow: '0 8px 40px rgba(185,28,28,0.5), inset 0 1px 0 rgba(255,255,255,0.15)',
                minWidth: 320,
              }}
            >
              {formatted && (
                <>
                  <span className="font-heading text-[11px] tracking-[0.3em] text-white/70 uppercase">{formatted.day}</span>
                  <span className="font-heading text-5xl sm:text-6xl font-black leading-none text-white my-1">{formatted.date}</span>
                  <span className="font-body text-xs tracking-[0.25em] text-white/80 uppercase">{formatted.month}</span>
                  {lockedEntry && (
                    <span className="font-mono text-[10px] text-primary-main/90 mt-2 font-semibold uppercase tracking-wider">
                      {lockedEntry.tournament_count} events
                    </span>
                  )}
                </>
              )}
              {/* Lock badge */}
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary-main flex items-center justify-center shadow-lg" title="Locked">
                <Lock className="w-4 h-4 text-black" strokeWidth={2.5} />
              </div>
            </div>
            {/* Unlock hint */}
            <button
              onClick={(e) => { e.stopPropagation(); onLockToggle(lockedDate); }}
              className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] text-white/60 hover:text-white text-[10px] tracking-[0.2em] uppercase transition-colors"
              data-testid="unlock-date-btn"
            >
              <Unlock className="w-3.5 h-3.5" />
              Unlock
            </button>
          </div>
        </div>
        <div className="h-px w-full bg-white/[0.04]" />
      </nav>
    );
  }

  // NORMAL MODE — centered, scrollable strip with double-click to lock.
  return (
    <nav data-testid="date-navigation" className="sticky top-0 z-40 backdrop-blur-sm" style={{ background: 'rgba(12,6,6,0.75)' }}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 py-4">
          <button onClick={() => scroll(-1)} className="flex-shrink-0 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all" data-testid="date-nav-scroll-left">
            <ChevronLeft className="w-4 h-4" />
          </button>

          {/* Scrollable + centered when content fits */}
          <div ref={scrollRef} className="flex-1 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="flex gap-1.5 justify-center min-w-max mx-auto">
              {displayDates.map((d) => {
                const formatted = formatDate(d.date);
                const isActive = selectedDate === d.date;
                return (
                  <button
                    key={d.date}
                    data-testid={`date-nav-tab-${d.date}`}
                    onClick={() => onDateSelect(d.date)}
                    onDoubleClick={() => onLockToggle(d.date)}
                    title="Click to select, double-click to lock"
                    className={`relative flex flex-col items-center px-4 sm:px-5 py-2 rounded-lg transition-all duration-300 min-w-[72px] select-none ${
                      isActive
                        ? 'text-white scale-105'
                        : 'text-white/40 hover:text-white/70 hover:bg-white/[0.03]'
                    }`}
                    style={isActive ? {
                      background: 'linear-gradient(135deg, #b91c1c 0%, #8b0000 100%)',
                      boxShadow: '0 4px 20px rgba(185,28,28,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
                    } : {}}
                  >
                    <span className="font-heading text-[9px] tracking-[0.15em] opacity-70">{formatted.day}</span>
                    <span className="font-heading text-lg sm:text-xl font-bold leading-tight">{formatted.date}</span>
                    <span className="font-body text-[9px] tracking-wider opacity-60">{formatted.month}</span>
                    {isActive && (
                      <span className="font-mono text-[8px] text-primary-main mt-0.5 font-semibold">{d.tournament_count} events</span>
                    )}
                    {isActive && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-main" />}
                  </button>
                );
              })}
            </div>
          </div>

          <button onClick={() => scroll(1)} className="flex-shrink-0 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all" data-testid="date-nav-scroll-right">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="h-px w-full bg-white/[0.04]" />
    </nav>
  );
};
